<template>
  <g>
    <!-- Define linear gradient for item -->
    <defs>
      <linearGradient
        v-for="(task, index) in tasks"
        :key="`gradient-${index}`"
        :id="index"
        x1="0%"
        x2="30%"
        y1="0%"
        y2="0%"
        gradientUnits="userSpaceOnUse"
      >
        <stop stop-color="blue" offset="0%"></stop>
        <stop stop-color="blue" offset="25%"></stop>
        <stop stop-color="transparent" offset="37%"></stop>
      </linearGradient>
    </defs>

    <!-- outer box -->
    <rect
      :x="calenderXOffset"
      :y="orderOffset"
      :height="heightBasedOnWindow * (tasks.length)"
      :width="24 * hourWidth"
      style="stroke: #ddd"
    ></rect>

    <!-- Vertical rectangles -->
    <rect
      v-for="(task, index) in tasks"
      :x="calenderXOffset"
      :key="`${index}-vert-outer`"
      :y="(index) * heightBasedOnWindow + orderOffset"
      :height="heightBasedOnWindow"
      :width="24 * hourWidth"
      style="fill: #fff;">
    </rect>

    <!-- Vertical lines -->
    <g
      v-for="(time, index) in times"
      :key="index"
    >
      <line
        :x1="index * hourWidth + calenderXOffset"
        :x2="index * hourWidth + calenderXOffset"
        :y1="orderOffset" :y2="lineHeight + orderOffset"
        stroke="#ddd">
      </line>
    </g>

    <!-- Render items -->
    <g v-for="(item, itemIndex) in tasks" :key="`${itemIndex}-item`">

      <!-- Dashed line between items -->
      <line
        :x1="calenderXOffset"
        :x2="calenderXOffset + 24 * hourWidth"
        :y1="orderOffset + (itemIndex * heightBasedOnWindow)"
        :y2="orderOffset + (itemIndex * heightBasedOnWindow)"
        stroke="#ddd"
        stroke-dasharray="4"
        v-if="itemIndex !== tasks.length"
      />

      <!-- Render Zones -->
      <g
        v-for="(zone, zoneIndex) in item.zones"
        :key="`${zoneIndex}-zone`"
      >
        <!-- Zone -->
        <rect
          class="calendar-zone"
          :ref="`event-${zone.eventId}`"
          :class="{
                  'paused-effect': item.isPaused === true,
                  'highlight': highlightId === zone.eventId,
                  'calendar-zone-clickable': zone.type === 'missed' &&
                  zone.events.length === 1 &&
                  !zone.events[0].approvedBy
          }"
          :height="heightBasedOnWindow * 0.8"
          :width="timesToLength(zone.startTime, zone.endTime)"
          :y="(itemIndex * heightBasedOnWindow) + (heightBasedOnWindow * 0.1) + orderOffset"
          :x="getXFromTime(zone.startTime)"
          :fill="zone.backgroundFill || zone.fill"
          style="fill-opacity: 0.3"
          :rx="heightBasedOnWindow * 0.4"
          @click="zone.type === 'missed' && !zone.events[0].approvedBy ?
          showMarkerTooltip(item.id, zone) :
          null"
        >
        </rect>
        <!-- Zone timestamps -->
        <g class="calendar-zone-timestamps">
          <!-- Start timestamp -->
          <line
            :x1="getXFromTime(zone.startTime)"
            :x2="getXFromTime(zone.startTime)"
            :y1="(itemIndex * heightBasedOnWindow) +
             2 + (heightBasedOnWindow / 2) + orderOffset"
            :y2="(itemIndex * heightBasedOnWindow) + (heightBasedOnWindow / 4) +
                  ((heightBasedOnWindow * 0.10) / 2) + 40 + orderOffset"
            :stroke="zone.fill"
            stroke-opacity="0.5"
          ></line>
          <text
            :x="getXFromTime(zone.startTime)"
            :y="(itemIndex * heightBasedOnWindow) + (heightBasedOnWindow / 4) +
                  ((heightBasedOnWindow * 0.10) / 2) + 50 + orderOffset"
            dominant-baseline="middle"
            text-anchor="middle"
            font-size="12"
          >{{ dateToHumanTime(zone.startTime) }}</text>

          <!-- End timestamp -->
          <line
            :x1="getXFromTime(zone.endTime)"
            :x2="getXFromTime(zone.endTime)"
            :y1="(itemIndex * heightBasedOnWindow)
             + 2 + (heightBasedOnWindow / 2) + orderOffset"
            :y2="(itemIndex * heightBasedOnWindow) + (heightBasedOnWindow / 4) +
                  ((heightBasedOnWindow * 0.10) / 2) + 40 + orderOffset"
            :stroke="zone.fill"
            stroke-opacity="0.5"
          ></line>
          <text
            :x="getXFromTime(zone.endTime)"
            :y="(itemIndex * heightBasedOnWindow) + (heightBasedOnWindow / 4) +
                  ((heightBasedOnWindow * 0.10) / 2) + 50 + orderOffset"
            dominant-baseline="middle"
            text-anchor="middle"
            font-size="12"
          >{{ dateToHumanTime(zone.endTime) }}</text>
        </g>
      </g>

      <!-- Render overdue lines -->
      <rect
        v-for="(overdue, index) in item.overdues"
        :key="index"
        :y="(itemIndex * heightBasedOnWindow) + orderOffset + (heightBasedOnWindow / 4)"
        :x="getXFromTime(overdue.start)"
        :width="timesToLength(overdue.start, overdue.end)"
        :height="heightBasedOnWindow / 2"
        fill="#FF454B"
        :rx="heightBasedOnWindow / 4"
      ></rect>

      <!-- Render markers -->
      <g
        v-for="(marker, markerIndex) in item.markers"
        :key="`${markerIndex}-marker`"
      >

        <!-- Marker circle -->
        <circle
          v-if="(marker.type !== 'extra' || !approvedDeviationsOnly) &&
          marker.type !== 'fullOverdue' && marker.type !== 'overdueRest'"
          :ref="`event-${marker.events[0].id}`"
          :class="trueHighlightId &&
                trueHighlightId === marker.events[0].id ? 'highlight' : ''"
          class="calendar-marker"
          :r="trueHighlightId && trueHighlightId === marker.events[0].id ?
          heightBasedOnWindow / 2.5 :
          heightBasedOnWindow / 3"
          :fill="marker.fill"
          :cx="getXFromTime(marker.timestamp)"
          :cy="(itemIndex * heightBasedOnWindow) + (heightBasedOnWindow / 2) + orderOffset"
          :style="marker.stroke ? `stroke: ${marker.stroke}; stroke-width: 4px;` : ''"
          @click="showMarkerTooltip(item.id, marker)"
        >
        </circle>

        <!-- Marker inner icon -->
        <text
          v-if="(marker.type !== 'extra' || !approvedDeviationsOnly) &&
          marker.type !== 'fullOverdue'"
          class="calendar-marker-icon"
          :x="getXFromTime(marker.timestamp)"
          :y="(itemIndex * heightBasedOnWindow) + (heightBasedOnWindow / 2) + orderOffset + 2"
          v-html="marker.icon ? marker.icon :
                marker.events.length > 1 ? marker.events.length : ''"
          fill="#fff"
          dominant-baseline="middle"
          text-anchor="middle"
        ></text>

        <!-- Note indication circle -->
        <circle
          v-if="marker.hasNotes"
          :class="trueHighlightId &&
                trueHighlightId === marker.events[0].id ? 'highlight' : ''"
          class="calendar-marker"
          :r="8"
          fill="#6484ff"
          :cx="getXFromTime(marker.timestamp) + 12"
          :cy="(itemIndex * heightBasedOnWindow) + (heightBasedOnWindow / 4) + orderOffset"
        >
        </circle>

        <!-- Note indication icon -->
        <svg v-if="marker.hasNotes"
             :x="getXFromTime(marker.timestamp) + 8"
             :y="(itemIndex * heightBasedOnWindow) + orderOffset + 8"
             height="9px"
             width="9px"
             viewBox="0 0 24 24">
          <path
            fill="#fff"
            d="M9,22A1,1 0 0,1 8,21V18H4A2,2 0 0,1 2,16V4C2,2.89 2.9,2 4,2H20A2,
            2 0 0,1 22,4V16A2,2 0 0,1 20,18H13.9L10.2,21.71C10,
            21.9 9.75,22 9.5,22V22H9Z" />
        </svg>
      </g>
    </g>

    <!-- Render current time line -->
    <text
      ref="nowMarker"
      v-if="isSameDay(currentTime, date)"
      :x="getXFromTime(currentTime)"
      :y="boxHeight + orderOffset + 4"
      text-anchor="middle"
      dominant-baseline="hanging"
      font-size="12px"
      color="#6484ff"
    >{{ $t('Now') }}</text>
    <line
      v-if="isSameDay(currentTime, date)"
      :x1="getXFromTime(currentTime)"
      :x2="getXFromTime(currentTime)"
      :y1="orderOffset"
      :y2="(heightBasedOnWindow * tasks.length) + orderOffset"
      stroke="#6484ff"
      stroke-opacity="0.7"
    ></line>

    <!-- Black top and bottom black lines -->
    <line
      x1="0"
      :x2="calenderXOffset + 24 * hourWidth"
      :y1="orderOffset + 1"
      :y2="orderOffset + 1"
      stroke="#000"
    />
    <!--<line
      x1="0"
      x2="2600"
      :y1="orderOffset + boxHeight"
      :y2="orderOffset + boxHeight"
      stroke="#000"
    />-->
  </g>
</template>

<script>
/* eslint-disable */

import {
  addSeconds, differenceInSeconds, format,
  isSameDay, isWithinInterval, startOfDay, subSeconds, isBefore, endOfDay,
  isAfter, fromUnixTime,
} from 'date-fns';
import { getLanguagePackFromBrowser } from '../../globals/helpers/language';

export default {
  props: {
    tasks: {
      type: Array,
      default: () => [],
      required: true,
    },
    date: {
      type: Date,
      required: true,
    },
    order: {
      type: Number,
      default: 0,
      required: true,
    },
    hourWidth: {
      type: Number,
      default: 100,
      required: false
    },
    approvedDeviationsOnly: {
      type: Boolean,
      default: false,
      required: false,
    },
    scaleDown: {
      type: Boolean,
      required: false,
      default: false
    },
    scaleUp: {
      type: Boolean,
      required: false,
      default: false
    },
    currentTime: {
      type: Number,
      required: true,
    },
    highlightId: {
      type: String,
      required: false,
      default: null,
    }
  },
  data() {
    return {
      windowWidth: window.innerWidth,
      calenderXOffset: 200,
      trueHighlightId: null,
      times: [
        '',
        '01:00',
        '02:00',
        '03:00',
        '04:00',
        '05:00',
        '06:00',
        '07:00',
        '08:00',
        '09:00',
        '10:00',
        '11:00',
        '12:00',
        '13:00',
        '14:00',
        '15:00',
        '16:00',
        '17:00',
        '18:00',
        '19:00',
        '20:00',
        '21:00',
        '22:00',
        '23:00',
        '',
      ],
    }
  },
  computed: {
    heightBasedOnWindow() {
      return 50;
    },
    lineHeight() {
      return (this.tasks.length) * this.heightBasedOnWindow;
    },
    boxHeight() {
      return (this.tasks.length) * this.heightBasedOnWindow;
    },
    orderOffset() {
      return this.boxHeight * this.order;
    },
  },
  watch: {
    highlightId: {
      handler(newVal) {
        if (newVal) {
          let firstEventId;
          this.tasks.forEach(task => {
            if (firstEventId) return;
            task.markers.forEach(marker => {
              if (firstEventId) return;
              if (marker.events.find(event => event.id === newVal)) {
                firstEventId = marker.events[0].id;
              }
            });
            if (!firstEventId) {
              task.zones.forEach(zone => {
                if (firstEventId) return;
                if (zone.events.find(event => event.id === newVal)) {
                  firstEventId = zone.events[0].id;
                }
              })
            }
          })

          if (firstEventId) {
            this.trueHighlightId = firstEventId;
            this.$refs[`event-${firstEventId}`][0].scrollIntoView({ block: 'center', inline: 'center', behavior: 'smooth' })
          }
        } else {
          this.trueHighlightId = null;
        }
      }
    }
  },
  mounted() {
    if (isSameDay(this.currentTime, this.date)) {
      this.$refs.nowMarker.scrollIntoView({ block: 'center', inline: 'center', behavior: 'smooth' });
    }
  },
  methods: {
    isBefore,
    isSameDay,
    startOfDay,
    endOfDay,
    addSeconds,
    isAfter,
    dateToHumanDate(date) {
      return format(date, 'do MMMM')
    },
    dateToHumanWeekDay(date) {
      return format(date, 'EEEE')
    },
    dateToHumanTime(date) {
      return format(date, 'HH:mm', { locale: getLanguagePackFromBrowser() });
    },
    timesToLength(start, end) {
      let endTime = end;
      if (!isSameDay(start, end)) {
        endTime = endOfDay(start);
      }
      const seconds = Math.abs(differenceInSeconds(start, endTime));
      const secondsInADay = 86400;
      const totalPixels = 24 * this.hourWidth;
      // seconds required to generate 1 pixel (36 if totalPixels is 2400)
      const onePixel = secondsInADay / totalPixels;
      return Math.floor(seconds / onePixel);
    },
    getOverdueX(time) {
      if(!isSameDay(time, this.currentTime)) {
        return this.calenderXOffset;
      } else {
        return this.getXFromTime(time);
      }
    },
    getOverdueWidth(start, end) {
      if(!isSameDay(start, end)) {
        return this.timesToLength(startOfDay(end), end);
      } else {
        return this.timesToLength(start, end)
      }
    },
    getXFromTime(start) {
      let timeShouldAdjust = false;
      // Adjustments for daylight savings
      try {
        let dayStart = startOfDay(start);
        const startOfDayOffset = dayStart.getTimezoneOffset();
        let endOffset;
        if (typeof start === 'number') {
          endOffset = fromUnixTime(start / 1000).getTimezoneOffset();
        } else {
          endOffset = start.getTimezoneOffset();
        }
        // if time has skipped forward
        if (startOfDayOffset > endOffset) {
          timeShouldAdjust = 'forwards';
        } else if (startOfDayOffset < endOffset) {
          timeShouldAdjust = 'backwards';
        }
      } catch(err) {
      }
      let seconds = Math.abs(differenceInSeconds(startOfDay(start), start));
      const secondsInAnHour = 3600;
      if (timeShouldAdjust === 'forwards') {
        seconds += secondsInAnHour;
      } else if (timeShouldAdjust === 'backwards') {
        seconds -= secondsInAnHour;
      }
      const secondsInADay = 86400;
      const totalPixels = 24 * this.hourWidth;
      // seconds required to generate 1 pixel (36 if totalPixels is 2400)
      const onePixel = secondsInADay / totalPixels;
      return Math.floor(seconds / onePixel) + this.calenderXOffset;
    },
    addActivity() {
      this.$router.push({ name: 'member-show-add-task', params: { userId: this.$route.params.userId } });
    },
    getMarkerSpecs(events) {
      const fulfilled = { fill: '#0dac30', icon: '&#10003' };
      const missed = { fill: '#FF454B', icon: '&#10005' };
      const extra = { fill: '#FF454B', icon: '&#10005' };
      const overdue = { fill: '#FF454B', icon: '&#10005' }
      const mixedGood = { fill: '#0dac30', icon: null };
      const mixedBad = { fill: '#FF454B', icon: null };

      if (events.length === 1) {
        const { type } = events[0];
        const approved = events[0].approvedBy;
        if (type === 'fulfilled') {
          return fulfilled;
        }
        if (type === 'missed') {
          return approved ? fulfilled : missed;
        }
        if (type === 'extra') {
          return approved ? fulfilled : extra;
        }
        if (type === 'overdue') {
          return overdue;
        }
      }

      if (events.find((event) => event.type !== 'fulfilled' && !event.approvedBy)) {
        return mixedBad;
      }
      return mixedGood;
    },
    showMarkerTooltip(taskId, marker) {
      this.$emit('showMarkerTooltip', {
        taskId,
        eventIds: marker.events.map(event => event.id),
      })
    },
  },
};
/* eslint-enable */
</script>

<style scoped lang="scss">
  .paused-effect {
    opacity: 0.3;
  }

  .calendar-marker {
    stroke-width: 0;
    cursor: pointer;
  }

  .calendar-marker:hover {
    stroke: #3ab43a;
    animation: zone-pulse 1.5s infinite ease-in-out;
  }

  .calendar-zone-clickable {
    cursor: pointer;
    stroke-width: 0;
  }

  .calendar-zone-clickable:hover {
    stroke: rgba(255, 69, 75, 0.2) !important;
    animation: zone-pulse 1.5s infinite ease-in-out;
  }

  @keyframes zone-pulse {
    0% {
      stroke-width: 0;
      stroke-opacity: 1;
    }

    70% {
      stroke-width: 16px;
      stroke-opacity: 0;
    }

    100% {
      stroke-width: 0;
      stroke-opacity: 0;
    }
  }

  .calendar-marker-icon {
    pointer-events: none;
  }

  .calendar-zone:hover + .calendar-zone-timestamps {
    opacity: 1 !important;
  }

  .calendar-zone-timestamps {
    opacity: 0;
    transition: opacity 0.2s;
    pointer-events: none;
  }

  @keyframes highlight {
    0% { opacity: 0.3; }
    50% { opacity: 1; }
    100% { opacity: 0.3; }
  }

  .highlight {
    animation: highlight 2s linear infinite;
    fill-opacity: 1 !important;
  }

</style>
